import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Sparkles, Loader2 } from 'lucide-react';
import { DataSource } from '@/entities/all';
import { createSourceNode, getNodeCatalog, buildSourceMaster, listSources, getUploadSignedUrl, stripUtf8Bom } from '@/services/pipeline';

const FILE_KINDS = new Set(['file', 'csv', 'csv_upload', 'xlsx', 'xls', 'parquet', 'json', 'ndjson']);
const NON_CONN_FIELD = (f) => f.type === 'file' || ['source_name', 'target_name', 'file'].includes(f.name);

// One connection/brief field, rendered by its declared type. Checkbox fields
// get a real Switch (a text input would capture the boolean as a string);
// required fields are marked and enforced by the caller.
function FieldInput({ field, value, onChange, sources }) {
  const id = `cfg_${field.name}`;
  if (field.type === 'checkbox') {
    return (
      <div className="flex items-center gap-2 py-1">
        <Switch id={id} checked={Boolean(value)} onCheckedChange={v => onChange(field.name, v)} />
        <Label htmlFor={id}>{field.label || field.name}</Label>
      </div>
    );
  }
  const label = (
    <Label htmlFor={id}>
      {field.label || field.name}
      {field.required && <span className="text-red-500 ml-0.5">*</span>}
    </Label>
  );
  if (field.type === 'source_select') {
    // Pick an existing read-side source (used by the enrichment master's
    // "source to enrich" — replaces the old free-text numeric-id field).
    const opts = (sources || []).filter(s => s.origin === 'data_source' && s.kind !== 'enrichment');
    return (
      <div>
        {label}
        <Select value={value != null ? String(value) : ''} onValueChange={v => onChange(field.name, v)}>
          <SelectTrigger id={id}><SelectValue placeholder="Pick a source…" /></SelectTrigger>
          <SelectContent>
            {opts.map(s => (
              <SelectItem key={s.id} value={String(s.entity_id)}>{s.source_name} ({s.kind})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === 'textarea') {
    return (
      <div>
        {label}
        <textarea id={id} placeholder={field.placeholder || ''}
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm min-h-[80px]"
          value={value ?? ''} onChange={e => onChange(field.name, e.target.value)} />
      </div>
    );
  }
  return (
    <div>
      {label}
      <Input id={id} type={field.type === 'number' ? 'number' : (field.type === 'password' ? 'password' : 'text')}
        placeholder={field.placeholder || ''}
        value={value ?? ''} onChange={e => onChange(field.name, e.target.value)} />
    </div>
  );
}

// Compact preview of what a master built: notes, schema chips, sample rows.
function MasterPreview({ result }) {
  const cols = result?.schema?.columns || [];
  const rows = result?.sample_rows || [];
  const previewCols = cols.slice(0, 5).map(c => c.name);
  return (
    <div className="space-y-3 rounded-md border border-violet-300 bg-violet-50/50 dark:bg-violet-950/20 p-3">
      <div className="flex items-center gap-2 text-sm font-medium text-violet-700 dark:text-violet-300">
        <Sparkles size={16} /> Master verified this source live
      </div>
      {result.notes && <div className="text-xs text-page-muted">{result.notes}</div>}
      {cols.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {cols.slice(0, 14).map(c => (
            <span key={c.name} className="px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-[11px] font-mono">{c.name}</span>
          ))}
          {cols.length > 14 && <span className="text-[11px] text-page-muted">+{cols.length - 14} more</span>}
        </div>
      )}
      {rows.length > 0 && previewCols.length > 0 && (
        <div className="overflow-x-auto">
          <table className="text-xs w-full">
            <thead><tr>{previewCols.map(c => <th key={c} className="text-left pr-3 pb-1 font-medium">{c}</th>)}</tr></thead>
            <tbody>
              {rows.slice(0, 3).map((r, i) => (
                <tr key={i} className="border-t border-page-divider">
                  {previewCols.map(c => <td key={c} className="pr-3 py-1 max-w-[180px] truncate">{String(r[c] ?? '')}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function CreateDataSourceModal({ isOpen, onClose, onSuccess, tutorialDemo }) {
  // In tutorialDemo mode the modal opens for DISPLAY inside a spotlight tour:
  // fictional sample values, submit is a no-op — nothing is ever created.
  const getInitialState = () => ({
    source_name: tutorialDemo ? 'Sample Customer Orders' : '',
    description: tutorialDemo ? 'Monthly order export from the sample ERP' : '',
    kind: 'file',
    trust_score: 0.8,
    status: 'Active',
    is_golden_source: false,
    config: {},
  });

  const [formData, setFormData] = useState(getInitialState());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // File-kind sources: the actual file, uploaded on create (signed PUT →
  // ingest). Optional — without it the source is created metadata-only and
  // the file can be uploaded later from the detail page.
  const [sourceFile, setSourceFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Load source connectors (types + their connection fields) from the catalog —
  // so every source type is offered, not a hardcoded subset. Agentic connectors
  // (family 'agentic': api / scraper / enrichment / rss) are created through
  // their MASTER instead of a hand-typed connection form.
  const [connectors, setConnectors] = useState([]);
  const [sources, setSources] = useState([]); // for source_select brief fields
  const [brief, setBrief] = useState({});
  const [building, setBuilding] = useState(false);
  const [masterResult, setMasterResult] = useState(null);
  useEffect(() => {
    if (!isOpen) return;
    getNodeCatalog().then(cat => setConnectors((cat?.connectors || []).filter(c => c.role === 'load'))).catch(() => {});
    listSources().then(r => setSources(r?.sources || [])).catch(() => {});
  }, [isOpen]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleConfig = (key, value) => setFormData(prev => ({ ...prev, config: { ...prev.config, [key]: value } }));
  const handleBrief = (key, value) => setBrief(prev => ({ ...prev, [key]: value }));

  const stripLabel = (c) => (c.label || c.storage_type_out).replace(/^Load\s+/, '');
  const plainOptions = connectors.filter(c => c.family !== 'agentic').map(c => ({ value: c.storage_type_out, label: stripLabel(c) }));
  const agenticOptions = connectors.filter(c => c.family === 'agentic').map(c => ({ value: c.storage_type_out, label: stripLabel(c) }));
  const connector = connectors.find(x => x.storage_type_out === formData.kind);
  const isAgentic = connector?.family === 'agentic' && !!connector?.master;
  const isFile = FILE_KINDS.has(formData.kind);
  const isConn = !isFile && !!formData.kind && !isAgentic;
  const connFields = useMemo(() => {
    const fields = (connector?.config_schema?.fields || []).filter(f => !NON_CONN_FIELD(f));
    return fields.map(f => ({ name: f.name, label: f.label || f.name, type: f.type, placeholder: f.placeholder, required: f.required }));
  }, [connector]);

  const selectKind = (v) => {
    setFormData(prev => ({ ...prev, kind: v, config: {} }));
    setBrief({});
    setMasterResult(null);
    setSourceFile(null);
    setError(null);
  };

  const missingRequired = () => {
    if (isConn) return connFields.filter(f => f.required && !String(formData.config[f.name] ?? '').trim()).map(f => f.label);
    return [];
  };

  // Demo variant "agentic": open showing an AI-built connector's master panel.
  useEffect(() => {
    if (isOpen && tutorialDemo === 'agentic' && connectors.length) {
      const agentic = connectors.find((c) => c.family === 'agentic');
      if (agentic) setFormData((prev) => ({ ...prev, kind: agentic.storage_type_out, config: {} }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tutorialDemo, connectors.length]);

  const runMaster = async () => {
    if (tutorialDemo) return; // display-only in a tutorial (masters cost real LLM calls)
    setError(null);
    const bf = connector?.master?.brief_fields || [];
    const missing = bf.filter(f => f.required && !String(brief[f.name] ?? '').trim()).map(f => f.label);
    if (missing.length) { setError(`Please fill: ${missing.join(', ')}`); return; }
    setBuilding(true); setMasterResult(null);
    try {
      const res = await buildSourceMaster(formData.kind, brief);
      setMasterResult(res);
      if (res?.ok === false) setError(res.error || 'The master could not build this source.');
      else if (res?.suggested_name && !formData.source_name) handleChange('source_name', res.suggested_name);
    } catch (err) {
      setError(err?.message || 'Master build failed');
    } finally {
      setBuilding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (tutorialDemo) { onClose?.(); return; } // display-only in a tutorial
    setError(null);
    if (isAgentic && !masterResult?.ok) {
      setError(`Build the source with the ${connector.master.title} first — it verifies the source works before it is created.`);
      return;
    }
    const missing = missingRequired();
    if (missing.length) { setError(`Please fill: ${missing.join(', ')}`); return; }
    setIsSubmitting(true);
    try {
      // Every kind goes through the connection-aware endpoint (creates the
      // data_source row + its load node). File sources upload their file
      // right here when one is attached (signed PUT → ingest + schema
      // analysis); without a file they are created metadata-only and the
      // upload happens on the source's detail page.
      const cfg = isAgentic ? { ...(masterResult.config || {}) } : { ...formData.config };
      if (cfg.port) cfg.port = Number(cfg.port);
      let s3_uri;
      if (isFile && sourceFile) {
        setUploading(true);
        try {
          const cleanFile = await stripUtf8Bom(sourceFile);
          const signed = await getUploadSignedUrl(cleanFile.name);
          const putRes = await fetch(signed.url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/octet-stream' },
            body: cleanFile,
          });
          if (!putRes.ok) throw new Error(`File upload failed (HTTP ${putRes.status})`);
          s3_uri = signed.s3_uri;
        } finally {
          setUploading(false);
        }
      }
      const created = await createSourceNode({
        kind: formData.kind,
        source_name: formData.source_name,
        description: formData.description,
        config: cfg,
        ...(s3_uri ? { s3_uri } : {}),
      });
      // Trust/status/golden live on the generic entity — best-effort update.
      if (created?.source_id) {
        try {
          await DataSource.update(created.source_id, {
            trust_score: formData.trust_score,
            status: formData.status,
            is_golden_source: formData.is_golden_source,
          });
        } catch { /* non-fatal — source + node exist */ }
      }
      onSuccess();
      setFormData(getInitialState());
      setBrief({}); setMasterResult(null); setSourceFile(null);
    } catch (err) {
      console.error('Failed to create data source', err);
      setError(err?.message || 'Failed to create data source');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // In tutorialDemo the dialog must be NON-modal: Radix's modal mode sets
    // pointer-events:none on <body>, which would freeze the tutorial card
    // rendered outside the dialog (and trap focus away from the tour).
    <Dialog open={isOpen} onOpenChange={onClose} modal={!tutorialDemo}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        {...(tutorialDemo ? { "data-tutorial-demo-modal": "1", onInteractOutside: (e) => e.preventDefault() } : {})}
      >
        <DialogHeader>
          <DialogTitle>Create New Data Source</DialogTitle>
          <DialogDescription>Add a new source system to the data dictionary.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 pt-4">
          <div>
            <Label htmlFor="source_name">Source Name</Label>
            <Input id="source_name" value={formData.source_name} onChange={e => handleChange('source_name', e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="kind">Type</Label>
            <Select value={formData.kind} onValueChange={selectKind}>
              <SelectTrigger data-testid="source-type-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Connectors</SelectLabel>
                  {(plainOptions.length ? plainOptions : [{ value: 'file', label: 'File (CSV / XLSX)' }]).map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectGroup>
                {agenticOptions.length > 0 && (
                  <SelectGroup>
                    <SelectLabel className="flex items-center gap-1.5 text-violet-600 dark:text-violet-400">
                      <Sparkles size={12} /> AI-built sources
                    </SelectLabel>
                    {agenticOptions.map(o => (
                      <SelectItem key={o.value} value={o.value}>
                        <span className="flex items-center gap-1.5">{o.label}<Sparkles size={12} className="text-violet-500" /></span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* AGENTIC source: master flow — brief → build (live probe) → preview */}
          {isAgentic && (
            <div className="space-y-3 rounded-md border-2 border-violet-300 dark:border-violet-800 p-3" data-testid="master-panel">
              <div className="flex items-center gap-2 text-sm font-medium text-violet-700 dark:text-violet-300">
                <Sparkles size={16} /> {connector.master.title}
              </div>
              <p className="text-xs text-page-muted">{connector.master.tagline}</p>
              {(connector.master.brief_fields || []).map(f => (
                <FieldInput key={f.name} field={f} value={brief[f.name]} onChange={handleBrief} sources={sources} />
              ))}
              <Button type="button" variant="secondary" disabled={building} onClick={runMaster} data-testid="master-build">
                {building
                  ? (<span className="flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Master is probing the source…</span>)
                  : (<span className="flex items-center gap-2"><Sparkles size={14} /> Build with {connector.master.title}</span>)}
              </Button>
              {masterResult?.ok && <MasterPreview result={masterResult} />}
            </div>
          )}

          {/* Connection fields for plain connector kinds */}
          {isConn && (
            <div className="space-y-3 rounded-md border border-page-divider p-3">
              <div className="text-sm font-medium">Connection</div>
              {connFields.length === 0 && <div className="text-xs text-page-muted">No connection fields for this type.</div>}
              {connFields.map(f => (
                <FieldInput key={f.name} field={f} value={formData.config[f.name]} onChange={handleConfig} sources={sources} />
              ))}
            </div>
          )}
          {isFile && (
            <div className="space-y-2 rounded-md border border-page-divider p-3">
              <Label htmlFor="source_file">File</Label>
              <Input
                id="source_file"
                type="file"
                accept=".csv,.txt,.tsv,.dat,.psv,.xlsx,.xls,.parquet,.json,.ndjson"
                data-testid="create-source-file"
                onChange={e => setSourceFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-page-muted">
                {sourceFile
                  ? `${sourceFile.name} uploads when you create the source, and its schema is discovered automatically.`
                  : 'Attach a .csv / .txt / .xlsx / .parquet / .json file now, or create the source empty and upload from its detail page later. Delimited text of any shape is fine — delimiter, encoding, preamble and repeated page headers are detected.'}
              </p>
            </div>
          )}
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" value={formData.description} onChange={e => handleChange('description', e.target.value)} />
          </div>
          <div>
            <Label htmlFor="status">Status</Label>
            <Select value={formData.status} onValueChange={v => handleChange('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Maintenance">Maintenance</SelectItem>
                <SelectItem value="Deprecated">Deprecated</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Trust Score: {(formData.trust_score * 100).toFixed(0)}%</Label>
            <Slider value={[formData.trust_score]} onValueChange={v => handleChange('trust_score', v[0])} max={1} step={0.01} />
          </div>
          <div className="flex items-center space-x-2">
            <Switch id="is_golden_source" checked={formData.is_golden_source} onCheckedChange={v => handleChange('is_golden_source', v)} />
            <Label htmlFor="is_golden_source">Is this a golden source?</Label>
          </div>
          {error && <div className="text-sm text-red-600" data-testid="create-source-error">{error}</div>}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting || (isAgentic && !masterResult?.ok)}>
              {uploading ? 'Uploading file…' : isSubmitting ? 'Creating...' : 'Create Source'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
